import React from 'react';

const GeometryDiagram = ({ data }) => {
  if (!data) return null;

  const {
    vertices = [],
    sides = [],
    angles = [],
    circles = [],
    lines = [],
    showGrid = false,
    title = ''
  } = data;

  if (vertices.length === 0 && circles.length === 0 && lines.length === 0) return null;

  // Calculate bounds from vertices and circles
  const allX = vertices.map(v => v.x);
  const allY = vertices.map(v => v.y);
  circles.forEach(c => {
    allX.push(c.cx - c.r, c.cx + c.r);
    allY.push(c.cy - c.r, c.cy + c.r);
  });

  const pad = 1.5;
  const minX = Math.min(...allX, 0) - pad;
  const maxX = Math.max(...allX, 5) + pad;
  const minY = Math.min(...allY, 0) - pad;
  const maxY = Math.max(...allY, 5) + pad;

  const w = maxX - minX;
  const h = maxY - minY;

  // SVG coordinate transform: flip Y axis so positive Y goes up
  const sx = (x) => ((x - minX) / w) * 300;
  const sy = (y) => 300 - ((y - minY) / h) * 300;

  // Build a vertex lookup map
  const vMap = {};
  vertices.forEach(v => { if (v.label) vMap[v.label] = v; });

  // Find vertex by label
  const getV = (label) => vMap[label] || null;

  // Calculate midpoint of two vertices
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  // Calculate offset for side label (perpendicular offset)
  const sideOffset = (a, b, dist = 0.35) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { x: -dy / len * dist, y: dx / len * dist };
  };

  // Draw right angle square
  const rightAngleMarker = (vertex, v1, v2, size = 0.4) => {
    const d1x = v1.x - vertex.x, d1y = v1.y - vertex.y;
    const d2x = v2.x - vertex.x, d2y = v2.y - vertex.y;
    const l1 = Math.sqrt(d1x * d1x + d1y * d1y) || 1;
    const l2 = Math.sqrt(d2x * d2x + d2y * d2y) || 1;
    const u1 = { x: d1x / l1 * size, y: d1y / l1 * size };
    const u2 = { x: d2x / l2 * size, y: d2y / l2 * size };
    const p1 = { x: vertex.x + u1.x, y: vertex.y + u1.y };
    const p2 = { x: vertex.x + u1.x + u2.x, y: vertex.y + u1.y + u2.y };
    const p3 = { x: vertex.x + u2.x, y: vertex.y + u2.y };
    return `M${sx(p1.x)},${sy(p1.y)} L${sx(p2.x)},${sy(p2.y)} L${sx(p3.x)},${sy(p3.y)}`;
  };

  // Find adjacent vertices for angle marker
  const findAdjacentVertices = (vertexLabel) => {
    const adj = [];
    // Check polygon edges (consecutive vertices)
    for (let i = 0; i < vertices.length; i++) {
      if (vertices[i].label === vertexLabel) {
        const prev = vertices[(i - 1 + vertices.length) % vertices.length];
        const next = vertices[(i + 1) % vertices.length];
        if (prev) adj.push(prev);
        if (next) adj.push(next);
        break;
      }
    }
    // Also check explicit sides
    sides.forEach(s => {
      if (s.from === vertexLabel) {
        const v = getV(s.to);
        if (v && !adj.find(a => a.label === v.label)) adj.push(v);
      }
      if (s.to === vertexLabel) {
        const v = getV(s.from);
        if (v && !adj.find(a => a.label === v.label)) adj.push(v);
      }
    });
    return adj;
  };

  // Draw arc for angle
  const angleArc = (vertex, v1, v2, radius = 0.6) => {
    const a1 = Math.atan2(v1.y - vertex.y, v1.x - vertex.x);
    const a2 = Math.atan2(v2.y - vertex.y, v2.x - vertex.x);
    let start = a1, end = a2;
    let diff = end - start;
    if (diff < 0) diff += 2 * Math.PI;
    if (diff > Math.PI) {
      [start, end] = [end, start];
      diff = 2 * Math.PI - diff;
    }
    const largeArc = diff > Math.PI ? 1 : 0;
    const x1 = vertex.x + radius * Math.cos(start);
    const y1 = vertex.y + radius * Math.sin(start);
    const x2 = vertex.x + radius * Math.cos(end);
    const y2 = vertex.y + radius * Math.sin(end);
    const r = (radius / w) * 300;
    return `M${sx(x1)},${sy(y1)} A${r},${r} 0 ${largeArc} 0 ${sx(x2)},${sy(y2)}`;
  };

  // Angle label position
  const angleLabelPos = (vertex, v1, v2, dist = 1.0) => {
    const a1 = Math.atan2(v1.y - vertex.y, v1.x - vertex.x);
    const a2 = Math.atan2(v2.y - vertex.y, v2.x - vertex.x);
    let mid_a = (a1 + a2) / 2;
    let diff = a2 - a1;
    if (diff < 0) diff += 2 * Math.PI;
    if (diff > Math.PI) mid_a += Math.PI;
    return { x: vertex.x + dist * Math.cos(mid_a), y: vertex.y + dist * Math.sin(mid_a) };
  };

  return (
    <div className="diagram-container">
      {title && <div className="diagram-title">{title}</div>}
      <svg viewBox="0 0 300 300" className="geometry-svg" role="img" aria-label={title || 'Geometry diagram'}>
        {/* Grid */}
        {showGrid && Array.from({ length: Math.ceil(w) + 1 }, (_, i) => {
          const x = Math.floor(minX) + i;
          return <line key={`gx${i}`} x1={sx(x)} y1={0} x2={sx(x)} y2={300} stroke="#e0e0e0" strokeWidth="0.5" />;
        })}
        {showGrid && Array.from({ length: Math.ceil(h) + 1 }, (_, i) => {
          const y = Math.floor(minY) + i;
          return <line key={`gy${i}`} x1={0} y1={sy(y)} x2={300} y2={sy(y)} stroke="#e0e0e0" strokeWidth="0.5" />;
        })}

        {/* Polygon fill (if 3+ vertices) */}
        {vertices.length >= 3 && (
          <polygon
            points={vertices.map(v => `${sx(v.x)},${sy(v.y)}`).join(' ')}
            fill="rgba(33, 150, 243, 0.08)"
            stroke="#1976d2"
            strokeWidth="2"
          />
        )}

        {/* Explicit side lines (if no polygon or for extra edges) */}
        {vertices.length < 3 && sides.map((s, i) => {
          const a = getV(s.from), b = getV(s.to);
          if (!a || !b) return null;
          return (
            <line key={`side${i}`}
              x1={sx(a.x)} y1={sy(a.y)} x2={sx(b.x)} y2={sy(b.y)}
              stroke={s.color || '#1976d2'} strokeWidth="2"
              strokeDasharray={s.dashed ? '6,4' : 'none'}
            />
          );
        })}

        {/* Extra lines */}
        {lines.map((l, i) => (
          <line key={`ln${i}`}
            x1={sx(l.x1)} y1={sy(l.y1)} x2={sx(l.x2)} y2={sy(l.y2)}
            stroke={l.color || '#666'} strokeWidth={l.width || 1.5}
            strokeDasharray={l.dashed ? '6,4' : 'none'}
          />
        ))}

        {/* Circles */}
        {circles.map((c, i) => {
          const r = (c.r / w) * 300;
          return (
            <g key={`circ${i}`}>
              <circle cx={sx(c.cx)} cy={sy(c.cy)} r={r}
                fill={c.fill || 'rgba(33, 150, 243, 0.06)'}
                stroke={c.color || '#1976d2'} strokeWidth="2"
              />
              {c.label && (
                <text x={sx(c.cx)} y={sy(c.cy + c.r + 0.3)}
                  textAnchor="middle" className="diagram-label">{c.label}</text>
              )}
            </g>
          );
        })}

        {/* Angle markers */}
        {angles.map((a, i) => {
          const v = getV(a.vertex);
          if (!v) return null;
          const adj = findAdjacentVertices(a.vertex);
          if (adj.length < 2) return null;
          const v1 = adj[0], v2 = adj[1];

          return (
            <g key={`ang${i}`}>
              {a.showSquare ? (
                <path d={rightAngleMarker(v, v1, v2)}
                  fill="none" stroke="#e91e63" strokeWidth="1.5" />
              ) : (
                <path d={angleArc(v, v1, v2)}
                  fill="none" stroke="#e91e63" strokeWidth="1.5" />
              )}
              {a.value && (() => {
                const pos = angleLabelPos(v, v1, v2);
                return (
                  <text x={sx(pos.x)} y={sy(pos.y)}
                    textAnchor="middle" dominantBaseline="middle"
                    className="diagram-angle-label">{a.value}</text>
                );
              })()}
            </g>
          );
        })}

        {/* Side labels */}
        {sides.map((s, i) => {
          if (!s.label) return null;
          const a = getV(s.from), b = getV(s.to);
          if (!a || !b) return null;
          const m = mid(a, b);
          const off = sideOffset(a, b);
          return (
            <text key={`sl${i}`}
              x={sx(m.x + off.x)} y={sy(m.y + off.y)}
              textAnchor="middle" dominantBaseline="middle"
              className="diagram-side-label">{s.label}</text>
          );
        })}

        {/* Vertex labels */}
        {vertices.map((v, i) => {
          if (!v.label) return null;
          // Offset label away from centroid
          const cx = vertices.reduce((s, vt) => s + vt.x, 0) / vertices.length;
          const cy = vertices.reduce((s, vt) => s + vt.y, 0) / vertices.length;
          const dx = v.x - cx, dy = v.y - cy;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const offX = (dx / dist) * 0.5;
          const offY = (dy / dist) * 0.5;
          return (
            <g key={`v${i}`}>
              <circle cx={sx(v.x)} cy={sy(v.y)} r="3" fill="#1976d2" />
              <text x={sx(v.x + offX)} y={sy(v.y + offY)}
                textAnchor="middle" dominantBaseline="middle"
                className="diagram-vertex-label">{v.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default GeometryDiagram;
